<template>
  <PageSection
    class="training-section-hero"
    :bg="getBackground()"
  >
    <template #start>
      <div class="container">
        <div class="hero-content">
          <h1
            v-if="title"
            class="title"
            v-inview-animate:once="{name: 'fade-down', duration: 500, delay: 300}"
            v-html="title"
          ></h1>
          <h2
          v-if="subtitle"
          class="text-small"
          v-inview-animate:once="{name: 'fade-down', duration: 500, delay: 400}"
          v-html="subtitle"
        ></h2>
        </div>

        <div class="info-blocks">
          <div class="info-block info-block-left">
            <div v-if="companyFullName" class="info-item">
              <div v-if="companyFullNameLabel" class="info-label">{{ companyFullNameLabel }}</div>
              <div class="info-value">{{ companyFullName }}</div>
            </div>
            <div v-if="companyShortName" class="info-item">
              <div v-if="companyShortNameLabel" class="info-label">{{ companyShortNameLabel }}</div>
              <div class="info-value">{{ companyShortName }}</div>
            </div>
            <div v-if="companyCreationDate" class="info-item">
              <div v-if="companyCreationDateLabel" class="info-label">{{ companyCreationDateLabel }}</div>
              <div class="info-value">{{ companyCreationDate }}</div>
            </div>
            <div v-if="companyFounder" class="info-item">
              <div v-if="companyFounderLabel" class="info-label">{{ companyFounderLabel }}</div>
              <div class="info-value">{{ companyFounder }}</div>
            </div>
            <div v-if="companyLocation" class="info-item">
              <div v-if="companyLocationLabel" class="info-label">{{ companyLocationLabel }}</div>
              <div class="info-value">{{ companyLocation }}</div>
            </div>
          </div>

          <div class="info-block info-block-right">
            <div v-if="workScheduleTitle || workScheduleDescription || workScheduleWeekdays || workScheduleLunch" class="info-section">
              <div v-if="workScheduleTitle" class="info-item">
                <div class="info-label">{{ workScheduleTitle }}</div>
              </div>
              <div v-if="workScheduleDescription" class="info-item">
                <div class="info-value-large">{{ workScheduleDescription }}</div>
              </div>
              <div v-if="workScheduleWeekdays" class="info-item">
                <div class="info-value-large">{{ workScheduleWeekdays }}</div>
              </div>
              <div v-if="workScheduleLunch" class="info-item">
                <div class="info-value-large">{{ workScheduleLunch }}</div>
              </div>
            </div>

            <div v-if="contactPhone || contactEmail" class="info-section">
              <div v-if="contactSectionTitle" class="info-item">
                <div class="info-label">{{ contactSectionTitle }}</div>
              </div>
              <div v-if="contactPhone" class="info-item">
                <a :href="`tel:${contactPhone.replace(/[^0-9+]/g, '')}`" class="contact-link">
                  <Icon name="phone" />
                  {{ contactPhone }}
                </a>
              </div>
              <div v-if="contactEmail" class="info-item">
                <a :href="`mailto:${contactEmail}`" class="contact-link">
                  <Icon name="mail" />
                  {{ contactEmail }}
                </a>
              </div>
            </div>

            <div v-if="activityLocation1 || activityLocation2" class="info-section">
              <div v-if="activityLocationTitle" class="info-item">
                <div class="info-label">
                  <Icon name="pin" />
                  {{ activityLocationTitle }}
                </div>
              </div>
              <ul class="info-list">
                <li v-if="activityLocation1">{{ activityLocation1 }}</li>
                <li v-if="activityLocation2">{{ activityLocation2 }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>
  </PageSection>
</template>

<script>
import PageSection from '@/components/PageSection.vue';
import Icon from '@/components/Icon.vue';

export default {
  name: 'TrainingSectionHero',
  components: {
    PageSection,
    Icon,
  },
  emits: ['button-click'],
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    background: {
      type: Object,
      default: null,
    },
    backgroundMobile: {
      type: Object,
      default: null,
    },
    companyFullName: {
      type: String,
      default: '',
    },
    companyFullNameLabel: {
      type: String,
      default: '',
    },
    companyShortName: {
      type: String,
      default: '',
    },
    companyShortNameLabel: {
      type: String,
      default: '',
    },
    companyCreationDate: {
      type: String,
      default: '',
    },
    companyCreationDateLabel: {
      type: String,
      default: '',
    },
    companyFounder: {
      type: String,
      default: '',
    },
    companyFounderLabel: {
      type: String,
      default: '',
    },
    companyLocation: {
      type: String,
      default: '',
    },
    companyLocationLabel: {
      type: String,
      default: '',
    },
    workScheduleTitle: {
      type: String,
      default: '',
    },
    workScheduleDescription: {
      type: String,
      default: '',
    },
    workScheduleWeekdays: {
      type: String,
      default: '',
    },
    workScheduleLunch: {
      type: String,
      default: '',
    },
    contactPhone: {
      type: String,
      default: '',
    },
    contactEmail: {
      type: String,
      default: '',
    },
    contactSectionTitle: {
      type: String,
      default: '',
    },
    activityLocation1: {
      type: String,
      default: '',
    },
    activityLocation2: {
      type: String,
      default: '',
    },
    activityLocationTitle: {
      type: String,
      default: '',
    },
  },
  methods: {
    getBackground() {
      if (!this.background) return null;
      const isMobile = this.$store.getters.getBreakpoint === 'sm';
      return {
        background: (isMobile && this.backgroundMobile) ? this.backgroundMobile : this.background,
        backgroundMobile: this.backgroundMobile,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.training-section-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  box-sizing: border-box;
  width: 100%;
  color: color(basic-light);

  @include screen("sm", min) {
    min-height: calc(var(--vh, 1vh) * 100);
  }

  @include rule(
    padding-bottom,
    (
      xl: 0,
      lg: 0,
      md: 0,
      sm: 0,
    )
  );

  @include screen("xxl", min) {
    padding-bottom: 0;
  }

  ::v-deep(.container) {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    width: 100%;
    flex: 1;
    min-height: 0;
  }

  .hero-content {
    display: flex;
    flex-direction: column;
    width: 100%;

    @include wRule(
      margin-top,
      (
        xl: 195,
        lg: 120,
        md: 110,
        sm: 110,
      )
    );

    @include wRule(
      margin-bottom,
      (
        xl: 80,
        lg: 30,
        md: 35,
        sm: 45,
      )
    );
  }

  .title {
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;

    @include wRule(
      font-size,
      (
        xl: 45,
        lg: 30,
        md: 35,
        sm: 45,
      )
    );

    @include wRule(
      margin-top,
      (
        sm: 45,
      )
    );
  }

  .text-small {
    display: block;
    font-weight: 400;
    line-height: 1.6;

    @include wRule(
      width,
      (
        xl: 517,
        lg: 458,
        md: 411,
        sm: 100%,
      )
    );

    @include wRule(
      font-size,
      (
        xl: 20,
        lg: 18,
        md: 16,
        sm: 30,
      )
    );

    @include wRule(
      margin-top,
      (
        xl: 40,
        lg: 35,
        md: 30,
        sm: 30,
      )
    );

    @include screen("md", min) {
      max-width: 55%;
    }

    @include screen("sm", max) {
      max-width: 100%;
    }
  }

  .info-blocks {
    display: grid;
    grid-template-columns: 1fr 1fr;
    background: rgba(0, 0, 0, 0.32);

    @include screen("xl") {
      backdrop-filter: blur(15.5px);
    }

    @include screen("lg", max) {
      backdrop-filter: blur(10.333px);
    }

    @include wRule(
      gap,
      (
        xl: 60,
        lg: 50,
        md: 40,
        sm: 40,
      )
    );

    @include wRule(
      padding,
      (
        xl: 60 122 60 122,
        lg: 35 50 35 50,
        md: 30 40 30 40,
        sm: 40 40 40 40,
      )
    );

    @include screen("sm", max) {
      grid-template-columns: 1fr;
      backdrop-filter: blur(10px);
    }
  }

  .info-block {
    display: flex;
    flex-direction: column;
  }

  .info-block-left {
    .info-item {
      &:not(:last-child) {
        @include wRule(
          margin-bottom,
          (
            xl: 24,
            lg: 20,
            md: 18,
            sm: 24,
          )
        );
      }
    }
  }

  .info-section {
    display: flex;
    flex-direction: column;

    &:not(:last-child) {
      @include wRule(
        margin-bottom,
        (
          xl: 32,
          lg: 28,
          md: 24,
          sm: 32,
        )
      );
    }
  }

  .info-item {
    display: block;
    width: 100%;
    color: color(basic-light);
    font-weight: 400;
    line-height: 1.6;

    &:not(:last-child) {
      @include wRule(
        margin-bottom,
        (
          xl: 8,
          lg: 8,
          md: 8,
          sm: 12,
        )
      );
    }
  }

  .info-label {
    font-weight: 700;
    display: flex;
    align-items: center;
    color: color(basic-light);

    @include wRule(
      gap,
      (
        xl: 8,
        lg: 7,
        md: 6,
        sm: 8,
      )
    );

    @include wRule(
      margin-bottom,
      (
        xl: 4,
        lg: 4,
        md: 4,
        sm: 8,
      )
    );

    ::v-deep(.icon) {
      display: block;
      flex-shrink: 0;

      @include wRule(
        width,
        (
          xl: 28,
          lg: 24,
          md: 20,
          sm: 32,
        )
      );

      @include wRule(
        height,
        (
          xl: 28,
          lg: 24,
          md: 20,
          sm: 32,
        )
      );
    }
  }

  .info-value {
    color: color(basic-light);
  }

  .info-value-large {
    color: color(basic-light);
    font-weight: 400;
    line-height: 1.6;

    @include wRule(
      font-size,
      (
        xl: 20,
        lg: 18,
        md: 16,
        sm: 30,
      )
    );
  }

  .info-list {
    list-style: disc;
    color: color(basic-light);
    font-weight: 400;
    line-height: 1.6;
    margin: 0;
    padding: 0;

    @include wRule(
      font-size,
      (
        xl: 20,
        lg: 18,
        md: 16,
        sm: 30,
      )
    );

    @include wRule(
      padding-left,
      (
        xl: 30,
        lg: 26,
        md: 22,
        sm: 30,
      )
    );

    li {
      &:not(:last-child) {
        @include wRule(
          margin-bottom,
          (
            xl: 12,
            lg: 10,
            md: 8,
            sm: 12,
          )
        );
      }
    }
  }

  .contact-link {
    display: inline-flex;
    align-items: center;
    color: color(basic-light);
    text-decoration: none;
    transition: opacity 0.3s ease;

    @include wRule(
      gap,
      (
        xl: 8,
        lg: 7,
        md: 6,
        sm: 8,
      )
    );

    &:hover {
      opacity: 0.8;
    }

    ::v-deep(.icon) {
      display: block;
      flex-shrink: 0;

      @include wRule(
        width,
        (
          xl: 28,
          lg: 24,
          md: 20,
          sm: 32,
        )
      );

      @include wRule(
        height,
        (
          xl: 28,
          lg: 24,
          md: 20,
          sm: 32,
        )
      );
    }
  }

  .button-wrapper {
    display: block;
    width: 100%;
  }

  ::v-deep(.bg-media) {
    img {
      @include screen("md") {
        object-position: 70% top;
      }
    }
  }

  ::v-deep(.bg-media::after) {
    background-color: rgba(0, 0, 0, 0.1);
    background-image: linear-gradient(180deg,
      rgba(20, 24, 31, 0) 0%,
      rgba(20, 24, 31, 0.6) 100%);
  }
}
</style>
